import { BoldText, DemiLightText } from '@/lib/Text';
import React, { useMemo } from 'react';

interface RatingStarsProps {
    /** 평점 (0-5) */
    rating: number;
    /** 점수 텍스트 표시 여부 */
    showScore?: boolean;
    /** 별 크기 (px) */
    size?: number;
    /** 별 사이 간격 (px) */
    gap?: number;
    /** 커스텀 클래스명 */
    className?: string;
    /** 폰트 사이즈 옵션 */
    fs?: number;
    /** 점수만큼의 별만 표시 */
    showOnlyFilled?: boolean;
}

type GradientStarsProps = {
    rating: number;
    size: number;
    gap: number;
    showOnlyFilled: boolean;
};

export const GradientStars = ({ rating, size, gap, showOnlyFilled }: GradientStarsProps) => {
    // 별의 채워진 정도 계산 (전체 별 기준)
    const width = size;
    const height = size;
    const visibleStars = showOnlyFilled ? Math.max(0, Math.min(5, Math.floor(rating))) : 5;
    const totalWidth = visibleStars * width + Math.max(visibleStars - 1, 0) * gap;

    // 고유 ID 생성 (리렌더링 시에도 유지)
    const { maskId, gradientId } = useMemo(
        () => ({
            maskId: `star-mask-${Math.random().toString(36).substr(2, 9)}`,
            gradientId: `star-gradient-${Math.random().toString(36).substr(2, 9)}`,
        }),
        []
    );

    if (visibleStars === 0) {
        return null;
    }

    const fillPercentage = showOnlyFilled ? 100 : (rating / 5) * 100;

    return (
        <svg
            width={totalWidth}
            height={height}
            viewBox={`0 0 ${totalWidth} ${height}`}
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                {/* 그라데이션 정의 */}
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#397FF2" />
                    <stop offset="100%" stopColor="#265ABD" />
                </linearGradient>

                {/* 별 마스크 정의 */}
                <mask id={maskId}>
                    {Array.from({ length: visibleStars }).map((_, index) => {
                        const x = index * (width + gap);
                        return (
                            <g key={index} transform={`translate(${x}, 0)`}>
                                <path
                                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                    fill="white"
                                    transform={`scale(${width / 20} ${height / 20})`}
                                />
                            </g>
                        );
                    })}
                </mask>
            </defs>

            {/* 배경 별들 (회색) */}
            {Array.from({ length: visibleStars }).map((_, index) => {
                const x = index * (width + gap);
                return (
                    <g key={`bg-${index}`} transform={`translate(${x}, 0)`}>
                        <path
                            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                            fill="#E5E7EB"
                            transform={`scale(${width / 20} ${height / 20})`}
                        />
                    </g>
                );
            })}

            {/* 그라데이션이 적용된 별들 (마스크 사용) */}
            <rect
                x="0"
                y="0"
                width={totalWidth * (fillPercentage / 100)}
                height={height}
                fill={`url(#${gradientId})`}
                mask={`url(#${maskId})`}
            />
        </svg>
    );
};

export const RatingStars: React.FC<RatingStarsProps> = ({
    rating,
    showScore = true,
    size = 18,
    gap = 4,
    className = '',
    fs = 50,
    showOnlyFilled = false,
}) => {
    return (
        <div className={`flex flex-col items-start gap-2 ${className}`}>
            {/* 점수 표시 */}
            {showScore && (
                <div className="flex items-center gap-1">
                    <BoldText
                        fontSize={fs}
                        lineHeight={fs ? fs + 10 : undefined}
                        color={'#191F28'}
                        letterSpacing={-0.5}
                    >
                        {rating.toFixed(1)}
                    </BoldText>
                    <DemiLightText
                        fontSize={fs}
                        lineHeight={fs ? fs + 10 : undefined}
                        color={'#D1D2D4'}
                        letterSpacing={-0.4}
                    >
                        /5
                    </DemiLightText>
                </div>
            )}

            {/* 별점 */}
            <GradientStars rating={rating} size={size} gap={gap} showOnlyFilled={showOnlyFilled} />
        </div>
    );
};
